import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import {
  ID_ESTADO_PENDIENTE_PAGO,
  estadoPendientePercepciones,
  ESTADOS_COMPROBANTE,
  ESTADO_REVISION
} from '../../constants/pedidoEstados'
import { toNumber } from '../helpers/calcularPrecioTotal'
import { PasarelaMP } from '../reutilizables/pasarelaPagos/PasarelaMP'
import styles from './Compras.module.css'

const PAGO_EN_CTA_CTE = 3
const BADGE_CUENTA_CORRIENTE = {
  texto: 'Cuenta corriente',
  clase: 'statusCuentaCorriente',
  icono: 'fa-book'
}

const Comprobante = ({ vendedor, pedido, muestraPasarela }) => {
  const itemsDelPedido = (pedido.pedidoItems || [])
    .map((pedidoItem) => {
      const itemCheckout = (vendedor.pedidoItems || []).find(
        (item) => item.idPublicacion === pedidoItem.idPublicacion
      )

      return itemCheckout ? { itemCheckout, pedidoItem } : null
    })
    .filter(Boolean)

  const items = {
    items: itemsDelPedido.map(({ itemCheckout, pedidoItem }) => ({
      title: itemCheckout.titulo,
      quantity: itemCheckout.cantidad,
      unit_price:
        itemCheckout.precioUnitario *
        (1 + itemCheckout.alicuota / 100) *
        ((itemCheckout.anticipo || 100) / 100),
      fee_percentage: toNumber(pedidoItem.feePercentage)
    })),
    orderId: pedido.idPedido,
    metadata: { pedidoItems: itemsDelPedido.map(({ pedidoItem }) => pedidoItem.idPedidoItem) },
    email: vendedor.email,
    idVendedor: vendedor.idVendedor
  }

  const resolverEstadoComprobante = (idEstado, esCuentaCorriente) => {
    const base = ESTADOS_COMPROBANTE[idEstado] ?? ESTADO_REVISION
    const subtitulo =
      estadoPendientePercepciones.includes(idEstado) && !esCuentaCorriente
        ? 'En breve recibirás las percepciones definitivas para su aprobación.'
        : base.subtitulo

    return {
      ...base,
      subtitulo,
      badge: esCuentaCorriente ? BADGE_CUENTA_CORRIENTE : base.badge
    }
  }

  const idPedidoEstadoItem = pedido.pedidoItems[0]?.idPedidoItemEstado
  const esCuentaCorriente = pedido.pedidoItems[0]?.idPagoEstado === PAGO_EN_CTA_CTE
  const estado = resolverEstadoComprobante(idPedidoEstadoItem, esCuentaCorriente)
  const claseHeroIcon =
    estado.variante === 'exito' ? styles.heroIconExito : styles.heroIconPendiente

  const domicilio = vendedor.altura ? `${vendedor.calle} ${vendedor.altura}` : vendedor.calle

  const datosVendedor = [
    { icono: 'fa-envelope', label: 'Email', valor: vendedor.email },
    vendedor.telefono && { icono: 'fa-phone', label: 'Teléfono', valor: vendedor.telefono },
    vendedor.celular && { icono: 'fa-mobile', label: 'Celular', valor: vendedor.celular },
    { icono: 'fa-map-marker', label: 'Domicilio', valor: domicilio }
  ].filter(Boolean)

  return (
    <section className={styles.page} aria-labelledby={`comprobante-${pedido.idPedido}`}>
      {/* RESULTADO DE LA OPERACIÓN */}
      <div className={styles.hero}>
        <span className={`${styles.heroIcon} ${claseHeroIcon}`} aria-hidden="true">
          <i className={`fa ${estado.icono}`} />
        </span>

        <span className={`${styles.status} ${styles[estado.badge.clase]}`}>
          <i className={`fa ${estado.badge.icono}`} aria-hidden="true" />
          {estado.badge.texto}
        </span>

        <h1 id={`comprobante-${pedido.idPedido}`} className={styles.title}>
          {estado.titulo}
        </h1>
        {estado.subtitulo && <p className={styles.subtitle}>{estado.subtitulo}</p>}

        <Link className={styles.botonPedido} to="/Pedidos">
          Ver pedido #{pedido.idPedido}
          <i className="fa fa-arrow-right" aria-hidden="true" />
        </Link>
      </div>

      {/* DATOS DEL VENDEDOR */}
      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Vendedor</h2>
        <p className={styles.razonSocial}>{vendedor.razonSocial}</p>
        <dl className={styles.datos}>
          {datosVendedor.map(({ icono, label, valor }) => (
            <div key={label} className={styles.dato}>
              <span className={styles.datoIcono} aria-hidden="true">
                <i className={`fa ${icono}`} />
              </span>
              <div className={styles.datoTexto}>
                <span className={styles.fieldLabel}>{label}</span>
                <span className={styles.datoValor}>{valor}</span>
              </div>
            </div>
          ))}
        </dl>
      </div>

      {/* MERCADO PAGO */}
      {muestraPasarela && idPedidoEstadoItem === ID_ESTADO_PENDIENTE_PAGO && (
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>Pagar ahora</h2>
          <div className={styles.mpBox}>
            <PasarelaMP items={items} />
          </div>
        </div>
      )}
    </section>
  )
}

export default Comprobante

Comprobante.propTypes = {
  vendedor: PropTypes.shape({
    razonSocial: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    telefono: PropTypes.string,
    celular: PropTypes.string,
    calle: PropTypes.string.isRequired,
    altura: PropTypes.string,
    pedidoItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    idVendedor: PropTypes.number.isRequired
  }).isRequired,
  pedido: PropTypes.shape({
    idPedido: PropTypes.number.isRequired,
    pedidoItems: PropTypes.arrayOf(
      PropTypes.shape({
        idPagoEstado: PropTypes.number.isRequired,
        idPedidoItemEstado: PropTypes.number.isRequired,
        esTiendaOficial: PropTypes.bool.isRequired
      })
    ).isRequired
  }).isRequired,
  muestraPasarela: PropTypes.bool.isRequired
}
